/**
 * NotificationBus — single notification routing layer for all Engine → Client events.
 *
 * Replaces the fragmented pattern where multiple components each call
 * `engine.listenNotifications()`, creating separate transport subscriptions.
 * Instead, ONE transport subscription feeds this bus, which dispatches via:
 *
 *   1. **Exact match** — `bus.subscribe("event/execution.started", handler)`
 *   2. **Prefix match** — `bus.registerDomain("event/push", handler)` (catch-all for a domain)
 *
 * Architecture:
 *
 *   Transport   ──→  NotificationBus  ──→  plugin-execution-store (domain: event/execution, event/document, etc.)
 *   (single WS/       ├─→  editor-store (domain: event/mcp)
 *    Tauri event)      ├─→  push-store (domain: event/push)
 *                      ├─→  EnginePanel (subscribe: event/sidecar.*, event/execution.*)
 *                      └─→  plugin Renderers (subscribe: event/stream.chunk, etc.)
 */

import type { EngineNotification, EngineNotificationHandler, EngineService } from "../platform";
import { frontendLog } from "../utils/frontend-logger";

// ── Types ──

/** Wildcard handler receives (method, params) for any notification matching a prefix */
export type DomainHandler = (method: string, params: unknown) => void;

/** Typed handler for a specific method */
export type MethodHandler<T = unknown> = (params: T) => void;

export interface NotificationBus {
    /**
     * Register a domain router — calls handler for any notification whose method
     * starts with the given prefix.
     *
     * Each notification is dispatched to **at most one** domain (longest prefix wins).
     * Exact subscriptions are always dispatched in addition to the matched domain.
     *
     * @example
     *   bus.registerDomain("event/execution", (method, params) => { ... });
     *   bus.registerDomain("event/push", handlePushEvent);
     */
    registerDomain(prefix: string, handler: DomainHandler): () => void;

    /**
     * Subscribe to a specific notification method. Multiple handlers per method
     * are supported. Handlers fire in addition to any matching domain handler.
     *
     * @example
     *   const unsub = bus.subscribe("event/stream.chunk", (params) => { ... });
     *   return unsub; // cleanup
     */
    subscribe<T = unknown>(method: string, handler: MethodHandler<T>): () => void;

    /**
     * Connect the bus to the underlying `EngineService` transport.
     * This creates the single `listenNotifications` subscription.
     * Safe to call multiple times — reconnects if already connected.
     */
    connect(engine: EngineService): Promise<void>;

    /** Disconnect from the transport and remove all domain/method handlers */
    disconnect(): void;

    /** Check whether the bus is currently connected */
    readonly connected: boolean;

    /**
     * Manually dispatch a notification through the bus.
     * Used for synthetic events like `event/transport.reconnected`.
     */
    dispatch(notification: EngineNotification): void;
}

// ── Factory ──

export function createNotificationBus(): NotificationBus {
    // Domain routers: prefix → handler (sorted by prefix length descending for longest-match)
    const domains = new Map<string, DomainHandler>();
    let sortedDomains: [string, DomainHandler][] = [];

    // Exact method subscriptions: method → Set<handler>
    const subscriptions = new Map<string, Set<MethodHandler<unknown>>>();

    // Transport teardown
    let stopTransport: (() => void) | null = null;
    let isConnected = false;
    let currentEngine: EngineService | null = null;
    let connectPromise: Promise<void> | null = null;
    // Invalidates a pending listenNotifications() result when disconnect(),
    // reset, or a newer connection wins while the transport is still opening.
    let connectionGeneration = 0;

    function rebuildSortedDomains() {
        sortedDomains = [...domains.entries()].sort((a, b) => b[0].length - a[0].length);
    }

    function dispatch(notification: EngineNotification) {
        const { method, params } = notification;

        if (method === "event/transport.disconnected") {
            isConnected = false;
        } else if (method === "event/transport.reconnected") {
            isConnected = true;
        }

        // 1. Exact match subscriptions — always fire
        const exact = subscriptions.get(method);
        if (exact) {
            for (const handler of exact) {
                try {
                    handler(params);
                } catch (err) {
                    frontendLog.error(`[NotificationBus] Error in handler for ${method}`, err, { source: "engine" });
                }
            }
        }

        // 2. Prefix match — longest-prefix-first, at most one domain
        for (const [prefix, handler] of sortedDomains) {
            if (method.startsWith(prefix)) {
                try {
                    handler(method, params);
                } catch (err) {
                    frontendLog.error(`[NotificationBus] Error in domain handler for ${prefix}`, err, { source: "engine" });
                }
                break;
            }
        }
    }

    const bus: NotificationBus = {
        get connected() {
            return isConnected;
        },

        registerDomain(prefix: string, handler: DomainHandler): () => void {
            domains.set(prefix, handler);
            rebuildSortedDomains();
            return () => {
                if (domains.get(prefix) === handler) {
                    domains.delete(prefix);
                    rebuildSortedDomains();
                }
            };
        },

        subscribe<T = unknown>(method: string, handler: MethodHandler<T>): () => void {
            let set = subscriptions.get(method);
            if (!set) {
                set = new Set<MethodHandler<unknown>>();
                subscriptions.set(method, set);
            }
            const h = handler as MethodHandler<unknown>;
            set.add(h);
            return () => {
                set?.delete(h);
                if (set && set.size === 0) {
                    subscriptions.delete(method);
                }
            };
        },

        async connect(engine: EngineService): Promise<void> {
            // Idempotent: skip if already connected to the same engine
            if (isConnected && currentEngine === engine) return;

            // Serialize concurrent callers. Use a loop because multiple callers
            // can resume after the same promise and one of them may already have
            // started the next connection before this caller continues.
            while (connectPromise) {
                try {
                    await connectPromise;
                } catch {
                    // A failed transport must not prevent a later caller from
                    // retrying with the same or a different engine.
                }
                if (isConnected && currentEngine === engine) return;
            }

            const generation = ++connectionGeneration;
            const pendingConnection = (async () => {
                // Disconnect any existing subscription first
                if (stopTransport) {
                    stopTransport();
                    stopTransport = null;
                }
                isConnected = false;
                currentEngine = null;

                const transportHandler: EngineNotificationHandler = (notification) => {
                    if (generation !== connectionGeneration) return;
                    dispatch(notification);
                };

                const stop = await engine.listenNotifications(transportHandler);
                if (generation !== connectionGeneration) {
                    // disconnect/reset happened before the transport finished
                    // opening. Tear down the orphan instead of reviving the bus.
                    stop();
                    return;
                }
                stopTransport = stop;
                currentEngine = engine;
                isConnected = true;
                frontendLog.debug("[NotificationBus] Connected to transport", undefined, { source: "engine" });
            })();
            connectPromise = pendingConnection;

            try {
                await pendingConnection;
            } finally {
                if (connectPromise === pendingConnection) {
                    connectPromise = null;
                }
            }
        },

        disconnect(): void {
            connectionGeneration += 1;
            if (stopTransport) {
                stopTransport();
                stopTransport = null;
            }
            isConnected = false;
            currentEngine = null;
            connectPromise = null;
            domains.clear();
            subscriptions.clear();
            sortedDomains = [];
        },

        dispatch,
    };

    return bus;
}

// ── Singleton ──

let _busInstance: NotificationBus | null = null;

/**
 * Get the global NotificationBus singleton.
 * Created on first access. Use `connect()` to wire it to an EngineService.
 */
export function getNotificationBus(): NotificationBus {
    if (!_busInstance) {
        _busInstance = createNotificationBus();
    }
    return _busInstance;
}

/**
 * Reset the global bus (for testing or engine switch).
 * Disconnects and clears all handlers.
 */
export function resetNotificationBus(): void {
    if (_busInstance) {
        _busInstance.disconnect();
        _busInstance = null;
    }
}
